import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ChatConversation } from '../../services/chat-api.js';
import * as chatApi from '../../services/chat-api.js';

interface GroupedConversations {
  label: string;
  items: ChatConversation[];
}

@customElement('cp-chat-sidebar')
export class CpChatSidebar extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) conversations: ChatConversation[] = [];
  @property({ type: Number }) activeConversationId: number | null = null;

  @state() private _searchQuery = '';
  @state() private _filteredConversations: ChatConversation[] | null = null;
  @state() private _renamingId: number | null = null;
  @state() private _renameValue = '';

  private _searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._searchDebounceTimer !== null) {
      clearTimeout(this._searchDebounceTimer);
      this._searchDebounceTimer = null;
    }
  }

  private _onSearchInput(e: Event): void {
    const input = e.target as HTMLInputElement;
    this._searchQuery = input.value;

    if (this._searchDebounceTimer !== null) {
      clearTimeout(this._searchDebounceTimer);
    }

    if (!this._searchQuery.trim()) {
      this._filteredConversations = null;
      return;
    }

    this._searchDebounceTimer = setTimeout(() => {
      void this._doSearch(this._searchQuery);
    }, 300);
  }

  private async _doSearch(query: string): Promise<void> {
    try {
      const results = await chatApi.searchConversations(query);
      this._filteredConversations = results;
    } catch {
      this._filteredConversations = [];
    }
  }

  private _onNewChat(): void {
    this.dispatchEvent(new CustomEvent('conversation-new', { bubbles: true, composed: true }));
  }

  private _onSelectConversation(id: number): void {
    this.dispatchEvent(new CustomEvent('conversation-select', {
      detail: { id },
      bubbles: true,
      composed: true,
    }));
  }

  private _onTitleDblClick(conv: ChatConversation, e: Event): void {
    e.stopPropagation();
    this._renamingId = conv.id;
    this._renameValue = conv.title;
    setTimeout(() => {
      const input = this.querySelector<HTMLInputElement>(`[data-rename-id="${conv.id}"]`);
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  private _onRenameInput(e: Event): void {
    this._renameValue = (e.target as HTMLInputElement).value;
  }

  private _onRenameKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      void this._commitRename();
    } else if (e.key === 'Escape') {
      this._renamingId = null;
    }
  }

  private async _commitRename(): Promise<void> {
    if (!this._renamingId) return;
    const id = this._renamingId;
    const title = this._renameValue.trim();
    this._renamingId = null;
    if (!title) return;
    this.dispatchEvent(new CustomEvent('title-rename', {
      detail: { id, title },
      bubbles: true,
      composed: true,
    }));
  }

  private _groupConversations(conversations: ChatConversation[]): GroupedConversations[] {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000);
    const weekAgo = new Date(today.getTime() - 7 * 86400000);

    const groups: Record<string, ChatConversation[]> = {
      'Heute': [],
      'Gestern': [],
      'Letzte 7 Tage': [],
      'Aelter': [],
    };

    for (const conv of conversations) {
      const d = new Date(conv.updated_at);
      if (d >= today) {
        groups['Heute'].push(conv);
      } else if (d >= yesterday) {
        groups['Gestern'].push(conv);
      } else if (d >= weekAgo) {
        groups['Letzte 7 Tage'].push(conv);
      } else {
        groups['Aelter'].push(conv);
      }
    }

    return Object.entries(groups)
      .filter(([, items]) => items.length > 0)
      .map(([label, items]) => ({ label, items }));
  }

  private _onDeleteConversation(conv: ChatConversation, e: Event): void {
    e.stopPropagation();
    this.dispatchEvent(new CustomEvent('conversation-delete', {
      detail: { id: conv.id, title: conv.title },
      bubbles: true,
      composed: true,
    }));
  }

  private _renderConversationItem(conv: ChatConversation) {
    const isActive = conv.id === this.activeConversationId;
    const isRenaming = conv.id === this._renamingId;
    const itemClass = `cp-chat-sidebar__item${isActive ? ' cp-chat-sidebar__item--active' : ''}`;

    return html`
      <li class="${itemClass}" @click=${() => this._onSelectConversation(conv.id)}>
        ${isRenaming ? html`
          <input
            class="cp-chat-sidebar__rename-input"
            data-rename-id="${conv.id}"
            .value=${this._renameValue}
            @input=${this._onRenameInput}
            @blur=${() => void this._commitRename()}
            @keydown=${this._onRenameKeydown}
            @click=${(e: Event) => e.stopPropagation()}
          />
        ` : html`
          <div class="cp-chat-sidebar__item-title-row">
            ${conv.agent ? html`
              <span
                class="cp-chat-sidebar__agent-dot"
                style="background: ${conv.agent.color}"
                title="${conv.agent.name}"
              ></span>
            ` : nothing}
            <div
              class="cp-chat-sidebar__item-title"
              @dblclick=${(e: Event) => this._onTitleDblClick(conv, e)}
              title="${conv.title}"
            >${conv.title}</div>
          </div>
        `}
        <div class="cp-chat-sidebar__item-meta">
          ${conv.agent && !conv.agent.has_avatar ? html`
            <span class="cp-chat-sidebar__agent-name">${conv.agent.name}</span>
          ` : nothing}
          <span class="cp-chat-sidebar__item-date">${this._formatTime(conv.updated_at)}</span>
          <button
            class="cp-chat-sidebar__delete-btn"
            @click=${(e: Event) => this._onDeleteConversation(conv, e)}
            title="Konversation loeschen"
          >&times;</button>
        </div>
      </li>
    `;
  }

  private _formatTime(dateStr: string): string {
    const d = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (d >= today) {
      return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  }

  render() {
    const displayed = this._filteredConversations ?? this.conversations;
    const isSearching = this._searchQuery.trim().length > 0;

    return html`
      <div class="cp-chat-sidebar">
        <div class="cp-chat-sidebar__header">
          <button class="cp-chat-sidebar__new-btn" @click=${this._onNewChat}>
            + Neuer Chat
          </button>
        </div>

        <div class="cp-chat-sidebar__search">
          <input
            class="cp-chat-sidebar__search-input"
            type="text"
            placeholder="Suche..."
            .value=${this._searchQuery}
            @input=${this._onSearchInput}
          />
        </div>

        <ul class="cp-chat-sidebar__list">
          ${displayed.length === 0 ? html`
            <li class="cp-chat-sidebar__empty">
              ${this._searchQuery ? 'Keine Treffer' : nothing}
            </li>
          ` : isSearching
            ? displayed.map(conv => this._renderConversationItem(conv))
            : this._groupConversations(displayed).map(group => html`
                <li class="cp-chat-sidebar__section-label">${group.label}</li>
                ${group.items.map(conv => this._renderConversationItem(conv))}
              `)
          }
        </ul>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-chat-sidebar': CpChatSidebar;
  }
}
